// Workspace surface for the Workflow Engine runtime (kind: 'workflowcard'). Renders the
// WorkflowCard where list/draft/note cards render — the desktop side panel, the mobile card/UI
// mode, and (via HandsFreeView) the single hands-free page — so a workflow behaves like every
// other workspace card. Mirrors DraftPanel's outer shell exactly (optional reply strip on mobile +
// a header with the "⋯" session menu); the header title is reported by the card (engagement title,
// or "Pending Approvals" when no engagement is bound).
//
// Also hosts two additional DISTINCT surfaces:
//   `mode: 'settings'` — WorkflowSettingsCard (the tenant's whole workflow STRUCTURE editor,
//                         no engagement in the picture). Tabs: Statuses & Labels, Approvals &
//                         Gates, Authority Assignment, Transitions.
//   `mode: 'list'`     — CompanyWorkflowsListCard (the overview step before full settings).
//                         User clicks "Customize" to switch to the settings card.
import { useState } from 'react';
import WorkflowCard from '@/components/Workflow/WorkflowCard';
import WorkflowSettingsCard from '@/components/Workflow/WorkflowSettingsCard';
import CompanyWorkflowsListCard from '@/components/Workflow/CompanyWorkflowsListCard';
import { HistoryMenu, ReplyStrip, type RecentEntry } from '@/components/chat/UXView';
import { WorkspaceHeader } from '@/components/UI/WorkspaceHeader';

/** A workflow surface rendered in the workspace card area (kind: 'workflowcard'). `engagementId`
 *  is absent for the approvals-only surface (SAM's list_pending_approvals has no engagement scope)
 *  AND for the `settings` and `list` modes (no engagement in the picture at all). `rev` is bumped
 *  each time SAM re-opens/re-points this surface so the card looks again even if nothing
 *  invalidated its RTK Query cache; `messageId` links it to the turn's assistant reply.
 *  `mode`: undefined/'engagement' renders WorkflowCard (one engagement's live execution state);
 *          'settings' renders WorkflowSettingsCard (tenant's whole workflow STRUCTURE editor);
 *          'list' renders CompanyWorkflowsListCard (overview before editing). */
export interface WorkflowCardMessage {
  kind: 'workflowcard';
  id: string;
  engagementId?: string;
  rev?: number;
  messageId?: string;
  mode?: 'engagement' | 'settings' | 'list';
}

export const WorkflowPanel = ({
  workflow, messageStrip, onExpandStrip, isWaiting, history = [], onReplay, historyHasMore, onHistoryLoadMore, onCustomize, onOpenEngagement,
}: {
  workflow: WorkflowCardMessage;
  /** SAM's reply for this turn, shown above the card on the mobile card surface. */
  messageStrip: string;
  onExpandStrip?: () => void;
  /** True while SAM is composing a new reply for this turn — shows the typing-dots strip. */
  isWaiting?: boolean;
  history?: RecentEntry[];
  onReplay?: (e: RecentEntry) => void;
  historyHasMore?: boolean;
  onHistoryLoadMore?: () => void;
  /** Called when the user clicks "Customize" in the CompanyWorkflowsListCard — opens the
   *  WorkflowSettingsCard surface. */
  onCustomize?: () => void;
  /** Approvals-only surface: opens the clicked approval's engagement in the workspace instead of
   *  deciding it inline. */
  onOpenEngagement?: (engagementId: string) => void;
}) => {
  const [meta, setMeta] = useState<{ title: string } | null>(null);
  const isSettings = workflow.mode === 'settings';
  const isList = workflow.mode === 'list';
  const headerTitle = meta?.title ?? (isSettings ? 'How Your Team Works' : isList ? 'Company Processes' : 'Workflow');

  return (
    <div className="flex flex-col h-full min-h-0">
      <ReplyStrip text={messageStrip} onExpand={onExpandStrip} isWaiting={isWaiting} />
      <WorkspaceHeader
        title={headerTitle}
        actions={<HistoryMenu history={history} onReplay={onReplay} hasMore={historyHasMore} onLoadMore={onHistoryLoadMore} />}
      />
      <div className="flex-1 overflow-y-auto no-scrollbar px-3 py-3 min-h-0">
        {isSettings ? (
          <WorkflowSettingsCard
            key={workflow.id}
            rev={workflow.rev}
            onMeta={setMeta}
          />
        ) : isList ? (
          <CompanyWorkflowsListCard
            key={workflow.id}
            onCustomize={onCustomize ?? (() => {})}
          />
        ) : (
          // WorkflowCard handles both cases: with engagementId (workflow state for a specific
          // engagement) and without (approvals-only surface — list_pending_approvals has no
          // engagement scope). Full property/engagement detail lives in the UXView workspace.
          <WorkflowCard
            key={workflow.engagementId ?? workflow.id}
            engagementId={workflow.engagementId}
            rev={workflow.rev}
            onMeta={setMeta}
            onOpenEngagement={onOpenEngagement}
          />
        )}
      </div>
    </div>
  );
};

export default WorkflowPanel;
